import { useMemo } from 'react'

import { MentionedUser } from '@factorialco/f0-react/dist/experimental'
import {
  AtsGetRelevantHiringManagerInfoDocument,
  AtsNoteMentionHiringManagerFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

interface IMentionAccess {
  id: string
  employeeId?: string
  fullname: string
  picture: string
}

interface Props {
  candidateId: string
  atsJobPostingId?: string
}

const createMentionObject = (
  hiringManager: AtsNoteMentionHiringManagerFragment
): IMentionAccess => {
  return {
    id: hiringManager.access?.id ?? '0',
    employeeId: hiringManager.access?.employee?.id,
    fullname: hiringManager.access?.fullName || '',
    picture: hiringManager.access?.avatar || '',
  }
}

const createRichTextEditorMentionObject = (
  hiringManager: AtsNoteMentionHiringManagerFragment,
  navigation: Navigation<NavigationPaths>
): MentionedUser => {
  return {
    // TODO: Remove Number() when f0-react `MentionedUser.id` accepts string
    id: Number(hiringManager.access?.id ?? '0'),
    href: hiringManager.access?.employee
      ? navigation.employees.id.fullPath({ id: hiringManager.access?.employee?.id })
      : undefined,
    label: hiringManager.access?.fullName || '',
    image_url: hiringManager.access?.avatar || '',
  }
}

const useRelevantHiringManagerAccesses = ({ atsJobPostingId, candidateId }: Props) => {
  const { data, loading } = useQuery(AtsGetRelevantHiringManagerInfoDocument, {
    variables: {
      atsJobPostingId: atsJobPostingId ?? '0',
      atsCandidateId: candidateId ?? '0',
    },
  })

  const relevantAccesses = useMemo(() => {
    if (!data?.ats) return []

    const managers =
      atsJobPostingId
        ? data.ats.hiringManagersConnection?.nodes || []
        : [
            ...new Map(
              (data.ats.applicationsConnection?.nodes || [])
                .flatMap((app) => app.jobPosting?.hiringManagers || [])
                .filter((hm) => !hm.access?.isDeactivated)
                .map((hm) => [hm.access?.id, hm])
            ).values(),
          ]

    return managers.filter((hm) => !hm.access?.isDeactivated)
  }, [data, atsJobPostingId])

  return { relevantAccesses, loading }
}

export const useNotesMentions = (props: Props) => {
  const { relevantAccesses } = useRelevantHiringManagerAccesses(props)
  return relevantAccesses.map(createMentionObject)
}

export const useRichTextEditorNotesMentions = (props: Props) => {
  const { navigation } = useNavigation()
  const { relevantAccesses, loading } = useRelevantHiringManagerAccesses(props)

  return {
    accesses: relevantAccesses.map((hiringManager) =>
      createRichTextEditorMentionObject(hiringManager, navigation)
    ),
    loading,
  }
}
